import { useCallback, useEffect, useRef } from "react";
import { scopedCatalog } from "../leafDocuments";
import type { DocTypeConfig } from "../../api/client";
import { beginLiveDocLoad, endLiveDocLoad } from "../../services/liveDocLoadState";
import {
  populateBatchPlaceholder,
  scaffoldDocument,
  skeletonFromTuples,
  updateTitlePageFields,
} from "../../services/wordWriter";
import { enqueueWordWrite } from "../../services/wordWriteQueue";
import type { ProgressEvent } from "../../types";
import { resolveConfidentiality } from "../services/confidentiality";
import {
  hasTemplateCover,
  scaffoldFromTemplateRender,
  templateCoverScaffoldOverrides,
  writeTemplateCover,
  writeTitlePage,
} from "../services/templateCover";

export interface UseWordWriteQueueResult {
  /** Passed as onProgressEvent to useVariantCRun. */
  handleProgressEvent: (event: ProgressEvent) => void;
  /** Idempotency guard — reset to false on each new run start / reset.
   *  Callers mutate `.current` directly. A "draft more sections" continuation
   *  pre-arms it to `true` so an already-scaffolded doc is never rebuilt. */
  scaffoldedRef: React.MutableRefObject<boolean>;
  /** Insertion mode for the active run. Callers mutate `.current`.
   *  "section" — insert each section live; "batch" — backend assembles final docx.
   *
   *  NOTE: this no longer gates any Word write. It remains the LIFECYCLE flag
   *  (pane-close durability in useSessionBootstrap, restore auto-hydrate in
   *  useRunControl, run-bundle metadata) and is deliberately left at "batch".
   *  Live projection is driven by `liveProjectionRef` instead. */
  insertionModeRef: React.MutableRefObject<"section" | "batch">;
  /** Whether this run scaffolds the skeleton and receives live per-section
   *  writes. Set from `shouldLiveProject(docConfig, submissionMode)` at run
   *  start / restore. Callers mutate `.current`. */
  liveProjectionRef: React.MutableRefObject<boolean>;
  /** Wall-clock stamp of the most recent agent write. Passed to useWordEdits
   *  as agentWriteRef to suppress false-positive "you edited" events. */
  agentLastWriteAtRef: React.MutableRefObject<number>;
  /** Chain an arbitrary Word-write closure onto the process-wide single-writer
   *  queue. Re-exported from `services/wordWriteQueue` so existing callers
   *  (the bootstrap rehydrate, the redraft re-projection) are unchanged. */
  enqueueWordWrite: <T>(fn: () => Promise<T>, label?: string) => Promise<T>;
}

/**
 * Owns the Word-write side-effect path triggered by SSE progress events:
 *
 *   gate_start  → scaffoldDocument (once per run, idempotency via scaffoldedRef)
 *   draft_ready with payload_type:"title_fields" → updateTitlePageFields
 *
 * Section BODIES are deliberately NOT written here. They are re-projected from
 * the server SSOT by `VariantCShell.reprojectSectionIntoLiveDoc` on
 * `section_ready`, which is the same path regeneration and the regulatory audit
 * already use. Two reasons that path wins:
 *
 *   1. The Specification gate's `section_ready` carries NO `sections[]` (only a
 *      draft_preview), so writing from the SSE payload silently skipped every
 *      spec section.
 *   2. It already has the settle deferral, a bounded retry and the per-row
 *      `liveDocLoadBySection` tracker that shows the writer the section landed.
 *
 * All Word.run-bound writes are serialised through the process-wide queue in
 * `services/wordWriteQueue` to prevent concurrent Office.js context corruption.
 *
 * @param docConfig      — the active doc-type config; used by scaffoldDocument.
 * @param leafDocumentId — the eCTD leaf this document IS, when the doctype
 *   declares a `documents:` partition (CMC/CTD). Without it the scaffold would
 *   build the whole 65-section tree into a single leaf document.
 */
export function useWordWriteQueue(
  docConfig: DocTypeConfig | null,
  leafDocumentId?: string | null,
): UseWordWriteQueueResult {
  // Scaffolding idempotency guard. gate_start may fire multiple times
  // (per-gate); scaffold only once per run. Callers reset to false on
  // RESET_TO_IDLE / run start.
  const scaffoldedRef = useRef(false);

  // Lifecycle mode captured at run-start. No longer gates Word writes.
  const insertionModeRef = useRef<"section" | "batch">("batch");

  // Whether this run projects sections into the live doc.
  const liveProjectionRef = useRef(false);

  // Wall-clock stamp of the most recent agent write; consumed by
  // useWordEdits to suppress false-positive edit attribution.
  const agentLastWriteAtRef = useRef<number>(0);

  // Read docConfig / leafDocumentId through refs so handleProgressEvent's
  // identity does not flip whenever the shell rebuilds the config object
  // (re-fetch on bootstrap, memo invalidation). A stable callback identity
  // keeps downstream consumers — useVariantCRun's SSE subscription effect, in
  // particular — from re-subscribing on unrelated config-object churn.
  const docConfigRef = useRef(docConfig);
  useEffect(() => {
    docConfigRef.current = docConfig;
  }, [docConfig]);
  const leafDocumentIdRef = useRef(leafDocumentId ?? null);
  useEffect(() => {
    leafDocumentIdRef.current = leafDocumentId ?? null;
  }, [leafDocumentId]);

  const handleProgressEvent = useCallback((event: ProgressEvent) => {
    if (event.event_type === "gate_start" && !scaffoldedRef.current) {
      scaffoldedRef.current = true;

      // No live projection for this run — show the "we're drafting, open it
      // from Drafts when it's done" placeholder instead of a skeleton that
      // would never fill. Since live projection became the default for every
      // doc-type this is reached only for the structural opt-outs in
      // `shouldLiveProject`: a custom submission (the writer's own document),
      // a doctype with an empty skeleton, or a config that has not loaded yet.
      if (!liveProjectionRef.current) {
        const config = docConfigRef.current;
        const displayName = config?.display_name || "";
        const standard = config?.standard;
        const shouldAppendStandard = standard && !displayName.includes(standard);
        const docTypeLabel =
          [displayName, shouldAppendStandard ? `(${standard})` : null].filter(Boolean).join(" ") || "document";
        void enqueueWordWrite(() =>
          populateBatchPlaceholder(docTypeLabel).catch((err) => {
            console.warn("[useWordWriteQueue] batch placeholder failed (non-fatal):", err);
            // Allow a future gate_start to retry.
            scaffoldedRef.current = false;
          }),
        );
        return;
      }

      // Narrow to the one eCTD leaf this document IS before reading the
      // skeleton. A doctype with no `documents:` block comes back by reference,
      // unchanged, so the single-document doctypes are untouched.
      const config = scopedCatalog(docConfigRef.current, leafDocumentIdRef.current);
      if (!config) {
        scaffoldedRef.current = false;
        return;
      }
      // title_page is loosely typed on the DocTypeConfig contract; the YAML's
      // fields/confidentiality shape matches what scaffoldDocument expects.
      const titlePage = config.title_page as
        | {
            enabled?: boolean;
            title?: string | null;
            fields?: Array<{ label: string; fill: string } | string>;
            confidentiality?: import("../services/confidentiality").ConfidentialityConfig;
          }
        | undefined;

      // The add-in's own scaffold (every doctype but a client template).
      const scaffoldInOfficeJs = async (): Promise<number> => {
        const headings = await scaffoldDocument({
          skeleton: skeletonFromTuples(config.skeleton as [string, string, number][]),
          // Prefer the doc-type's title_page.title banner (the SSOT the
          // server docx renderer uses) so the live path and the Load path
          // show the same top-of-page title; fall back to display_name.
          documentTitle: (titlePage?.title || config.display_name)?.toUpperCase() || "",
          titlePageFields: titlePage?.fields,
          // Coerce the string-or-structured-mapping confidentiality to a
          // literal (or null = omit / undefined = default). Passing the raw
          // mapping straight to Office.js insertParagraph renders wrong.
          confidentiality: resolveConfidentiality(titlePage?.confidentiality),
          // A doc-type without a title page (CMC): no cover — a live run of
          // it never receives title_fields.
          omitCover: titlePage?.enabled === false,
          // Official Word numbering for live-drafted docs — same regime as
          // the server-rendered .docx. Suppressed when the doc-type opts out.
          // "template" (private workspace templates): numbers exactly as
          // authored — literal text, title-only for unnumbered_sections.
          nativeNumbering: config.heading_numbering !== "none" && config.heading_numbering !== "template",
          suppressHeadingNumbers: config.heading_numbering === "none",
          unnumberedSections: config.heading_numbering === "template" ? (config.unnumbered_sections ?? []) : [],
          // NO forceClear: scaffoldDocument then self-skips when the body
          // already holds >200 chars, so writer content can never be wiped
          // and a "draft more sections" continuation is a no-op.
          // A template with its own title page: no standard chrome; the
          // template page goes in below, before any field is drafted.
          ...templateCoverScaffoldOverrides(titlePage),
          // The look the download renders with (a client template's own).
          styleSpec: config.live_doc_style_spec,
        });
        // The title page "Load doc" shows (server-rendered, the download's
        // own) replaces the scaffold's chrome, which stays if the server has
        // none. A template with its own title page got no chrome at all.
        if (headings > 0 && titlePage?.enabled !== false) {
          await writeTemplateCover(config.document_type, {}).catch((err) => {
            console.warn("[useWordWriteQueue] server title page failed (non-fatal):", err);
          });
        }
        return headings;
      };

      // Serialised on the shared queue — NOT fired bare. A ~100-heading
      // scaffold takes seconds, and the first section re-projection lands well
      // inside that window; two overlapping Word.run batches corrupt each
      // other's paragraph indices. Bracketed by the live-doc load guard so the
      // audit re-projection (which checks isLiveDocLoading) stands down until
      // the headings it targets actually exist.
      void enqueueWordWrite(async () => {
        beginLiveDocLoad();
        try {
          // A client template: the server's own render of the empty document —
          // what "Load doc" inserts — so the template's headings, page setup,
          // header / footer and styles match a Load. Its title page is in it.
          // The Office.js scaffold below only when the server can't render it.
          const fromTemplate = config.live_doc_style_spec?._template
            ? await scaffoldFromTemplateRender(event.run_id, config)
            : null;
          const headings = fromTemplate ?? (await scaffoldInOfficeJs());
          if (headings === 0) {
            console.warn(
              "[useWordWriteQueue] scaffold skipped — document already populated; " +
                "live section writes will target the existing headings",
            );
          }
          agentLastWriteAtRef.current = Date.now();
        } catch (err) {
          console.warn("[useWordWriteQueue] scaffold failed (non-fatal):", err);
          // Allow a future gate_start to retry. Deliberately NOT done for the
          // headings===0 case above: that is a correct skip, not a failure.
          scaffoldedRef.current = false;
        } finally {
          endLiveDocLoad();
        }
      });

      return;
    }

    // Title-page modules deliver output via draft_ready with
    // payload_type:"title_fields". Serialised with the scaffold so it can't
    // race the skeleton's own title-table insertion. Live projection ONLY: the
    // scaffolded title page exists in the live doc and gets its fields filled
    // in place. Batch mode holds just the placeholder text — writing here
    // created a stray title table under it. Batch title fields reach the
    // assembled docx via the backend renderer and reach the live doc on Load.
    if (event.event_type === "draft_ready") {
      if (!liveProjectionRef.current) return;
      const resp = event.section_response as { payload_type?: string; fields?: Record<string, string> } | undefined;
      if (resp?.payload_type === "title_fields" && resp.fields) {
        const fields = resp.fields;
        // title_page.style + statement_fields ride the same loosely typed
        // doc-type config blob as the scaffold call above.
        const tpConfig = docConfigRef.current?.title_page as
          | {
              style?: import("../../services/wordWriter").TitlePageStyle;
              statement_fields?: string[];
            }
          | undefined;
        // Server-rendered (same page as "Load doc"); the add-in's own table
        // renderer only if the server can't — never over a template's page.
        void enqueueWordWrite(() =>
          writeTitlePage(docConfigRef.current?.document_type, fields, () =>
            hasTemplateCover(tpConfig)
              ? Promise.resolve()
              : updateTitlePageFields(fields, tpConfig?.style, tpConfig?.statement_fields),
          )
            .then(() => {
              agentLastWriteAtRef.current = Date.now();
            })
            .catch((err) => {
              agentLastWriteAtRef.current = Date.now();
              console.warn("[useWordWriteQueue] updateTitlePageFields failed (non-fatal):", err);
            }),
        );
      }
    }
  }, []);

  return {
    handleProgressEvent,
    scaffoldedRef,
    insertionModeRef,
    liveProjectionRef,
    agentLastWriteAtRef,
    enqueueWordWrite,
  };
}
